import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import { useParams, useRevalidator } from "react-router";
import React, { useEffect } from "react";
import AddToCalendar from "~/components/add-to-calendar";
import { graphql } from "~/graphql/codegen";
import { useMutation } from "@apollo/client/react";
import {
  MindspaceEventFragment,
  MindspaceEventRsvpMutation,
  MindspaceEventRsvpMutationVariables,
} from "~/graphql/codegen/graphql";
import useAuthCheck from "~/hooks/check-authenticated";
// import { useNotification } from "~/hooks/notification";
import {
  Button,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@mindstonehq/ui";
import { isFrontend, formatDateLong } from "~/utils";
import { useCurrentSpace } from "~/hooks/context-hook";
import MindspaceEventTimes from "./mindspace-event-times";
import metrics from "~/utils/metrics";
import { buildRsvpAttributionRedirect } from "./rsvp-referral-redirect";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

const MINDSPACE_EVENT_RSVP_MUTATION = graphql(`
  mutation MindspaceEventRSVP(
    $mindspaceIdentifier: ID!
    $eventIdentifier: ID!
    $attend: Boolean!
    $stripeSessionID: String
  ) {
    mindspaceEventRSVP(
      mindspaceIdentifier: $mindspaceIdentifier
      eventIdentifier: $eventIdentifier
      attend: $attend
      stripeSessionID: $stripeSessionID
    )
  }
`);

function EventDetails({
  event,
  variant = "default",
}: {
  event: MindspaceEventFragment;
  variant?: "default" | "attending";
}) {
  const frontend = isFrontend();
  const calendarVariant = variant === "attending" ? "ghost" : "outline";
  const calendarClassName = "w-full";
  const containerClassName =
    variant === "attending"
      ? "flex flex-col gap-3 mt-4"
      : "flex flex-col gap-3 mt-4 text-sm text-muted-foreground";

  return (
    <div className={containerClassName}>
      <div className="flex items-center space-x-1">
        <CalendarIcon className="h-5 w-5 text-tertiary" />
        <div className="hover:text-accent-foreground">
          <AddToCalendar
            events={[
              {
                id: event.id,
                name: event.name,
                startDatetime: event.startDatetime,
                endDatetime: event.endDatetime,
                description: event.eventDescription,
                joinUrl:
                  event.location?.__typename === "OnlineLocation"
                    ? event.location.joinUrl
                    : event.locationShort,
                publicUrl: event.publicUrl,
                agenda:
                  event.agenda?.map((x) => ({ title: x.title })) || [],
              },
            ]}
            downloadFilename={event.name}
            variant={calendarVariant}
            size="sm"
            className={calendarClassName}
            track={() => {
              metrics.track("live_session_calendar", {
                page: "event_rsvp",
                action: "add_event",
              });
            }}
          >
            {frontend && formatDateLong(event.startDatetime)}
          </AddToCalendar>
        </div>
      </div>
      <div className="flex items-center space-x-1">
        <ClockIcon className="h-5 w-5 text-tertiary mr-1" />
        {frontend && (
          <span>
            <MindspaceEventTimes event={event} />
          </span>
        )}
      </div>
      <div className="flex items-center space-x-1 mb-1">
        <MapPinIcon className="h-5 w-5 text-tertiary" />
        {event.location?.__typename === "GeoLocation" && (
          <a
            target="_blank"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              event.location.address,
            )}`}
            className="font-normal text-foreground hover:text-accent-foreground"
          >
            {event.location.address}
          </a>
        )}
        {event.location?.__typename === "OnlineLocation" && (
          <a
            target="_blank"
            href={event.location.joinUrl}
            className="font-normal text-foreground hover:text-accent-foreground"
          >
            {event.location.text}
          </a>
        )}
      </div>
    </div>
  );
}

const dialogFooterClassName = "flex-col-reverse gap-2 sm:flex-row sm:justify-end";
const dialogButtonClassName = "min-h-[44px] w-full sm:w-auto";

// ---------------------------------------------------------------------------
// ONE-OFF: Zoom registration funnel for a SINGLE event.
//
// This is intentional, hard-coded one-off functionality for ONE specific
// event only. When a user saves their spot for the event whose slug matches
// one of `ZOOM_FUNNEL_EVENT_SLUGS`, the success state hands them off to that
// event's external Zoom registration page via a real, copyable anchor link
// (Zoom collects phone + retreat questions on its own form). Every other event
// is completely unaffected.
//
// The gate matches this single event across BOTH prod and staging. The slugs
// differ between environments because the event's title differs there, so we
// list every environment-specific slug for the same underlying event. They all
// funnel into the same prod Zoom registration link below.
//
// This is deliberately NOT generalised. To support this for more than one
// event, lift this config (slug -> registration URL) into the internal admin
// platform rather than hard-coding it here.
// See: docs/plans/260623_zoom-registrant-funnel/PLAN.md
// ---------------------------------------------------------------------------
const ZOOM_FUNNEL_EVENT_SLUGS = [
  "fireside-chat-breaking-through-from-messy-middle-to-ai-native-2026", // prod
  "fireside-chat-breaking-through-the-ai-messy-middle-2026", // staging (s101) — event has a different title/slug there
];
const ZOOM_FUNNEL_REGISTRATION_URL =
  "https://us02web.zoom.us/meeting/register/4xqerjgnSlmJa2EcjMPbAA#/registration";

export default function MindspaceEventRSVPDialog({
  event,
}: {
  event: MindspaceEventFragment;
}) {
  const { t } = useLingui();
  const frontend = isFrontend();
  // ONE-OFF: see ZOOM_FUNNEL_* config block at module scope. True only for the
  // single fireside-chat event that funnels into external Zoom registration.
  const isZoomFunnelEvent = ZOOM_FUNNEL_EVENT_SLUGS.includes(event.slug);
  const [response, setResponse] = React.useState<"going" | "notGoing" | null>(
    null,
  );

  useEffect(() => {
    setResponse(null);
  }, []);

  const checkAuth = useAuthCheck();
  const [eventRSVP, { loading }] = useMutation(MINDSPACE_EVENT_RSVP_MUTATION);
  const currentSpace = useCurrentSpace();
  const uniqueName = currentSpace.uniqueName; // TODO: Fix hardcoded stuff here
  const { revalidate } = useRevalidator();
  const handleRSVP = (going: boolean) => {
    const ref =
      frontend ? new URLSearchParams(window.location.search).get("ref") : null;

    // Route referral RSVPs through the server loader so attribution and
    // post-RSVP redirect behavior stay aligned with the inline RSVP path.
    const postAuthRedirect = buildRsvpAttributionRedirect({
      eventSlug: event.slug,
      eventRedirectUrl: event.redirectURL,
      ref,
    });
    const authenticated = checkAuth(
      t(msg`Please log in to RSVP for this event.`),
      postAuthRedirect,
      "event_rsvp",
    );

    if (!authenticated) {
      return;
    }

    if (going) {
      // Track the RSVP action
      metrics.track("event_rsvp_fe", {
        action: "register",
        mindspace_id: currentSpace.id || "",
        event_id: event.id,
      });
    }

    // Paid events: a first registration must go through Stripe checkout, never
    // the free in-place RSVP mutation (the server rejects it anyway). Route to
    // the checkout loader. Already-registered users fall through so they can
    // still manage/withdraw their spot.
    const isPaid = Boolean(
      event.price?.isPaid && (event.price?.amountMinor ?? 0) > 0,
    );
    const isRegistered =
      event.attending || event.approvalStatus === "confirmed";
    if (going && isPaid && !isRegistered) {
      if (frontend) {
        window.location.href = `/events/${event.slug}/rsvp`;
      }
      return;
    }

    if (going && ref) {
      // When a referral code is present, use the server RSVP loader path so
      // RSVP + referral attribution happen together server-side.
      window.location.href = postAuthRedirect;
      return;
    }

    eventRSVP({
      variables: {
        mindspaceIdentifier: uniqueName || "",
        eventIdentifier: event.slug || "",
        attend: going,
      },
    })
      .then((resp) => {
        setResponse(going ? "going" : "notGoing");
        revalidate();

        // Redirect to redirectURL if it exists and user is RSVPing.
        // Suppressed for the Zoom-funnel one-off: that event opens Zoom in a
        // new tab instead of redirecting the current one.
        if (going && event.redirectURL && !isZoomFunnelEvent) {
          setTimeout(() => {
            window.location.href = event.redirectURL!;
          }, 1500); // Give user a moment to see the success message
        }
      })
      .catch((error) => {
        toast.error(t(msg`Error sending RSVP for event`), {
          description: t(msg`Please try again later`),
        });
      });
  };

  // --- Post-action response states ---

  if (response === "going") {
    // ONE-OFF (see ZOOM_FUNNEL_* config above): for the single Zoom-funnel
    // event we no longer auto-open Zoom. Instead we hand the user off with a
    // real, copyable anchor link ("Register on Zoom") so they can finish on
    // Zoom's own form to get their personal join link.
    const showZoomFunnelHandoff = isZoomFunnelEvent && !event.requiresApproval;
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {showZoomFunnelHandoff ? (
              <Trans>Nearly there</Trans>
            ) : (
              <Trans>You're in</Trans>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {event.requiresApproval
            ? event.approvalStatus === "approved" ||
              event.approvalStatus === "confirmed"
              ? t(msg`Spot confirmed. See you there.`)
              : t(msg`Request sent. We'll confirm soon.`)
            : showZoomFunnelHandoff
              ? t(
                  msg`One last step — register on Zoom to confirm your place. You'll get your personal join link by email.`,
                )
              : t(msg`Spot saved. See you there.`)}
        </DialogDescription>
        <DialogFooter className={dialogFooterClassName}>
          {!showZoomFunnelHandoff && (
            <DialogClose asChild>
              <Button
                variant="secondary"
                size="sm"
                className={dialogButtonClassName}
                onClick={() => {
                  setResponse(null);
                }}
              >
                <Trans>Done</Trans>
              </Button>
            </DialogClose>
          )}
          {showZoomFunnelHandoff && (
            // Funnel case: only "Register on Zoom". The dialog stays dismissable
            // via DialogContent's built-in ✕ (showCloseButton defaults true),
            // plus Escape / overlay click, so dropping "Done" leaves no trap.
            <Button asChild size="sm" className={dialogButtonClassName}>
              <a
                href={ZOOM_FUNNEL_REGISTRATION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t(msg`Register on Zoom`)}
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    );
  }
  if (response === "notGoing") {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>All good</Trans></DialogTitle>
        </DialogHeader>
        <DialogDescription><Trans>No stress - catch you at another one.</Trans></DialogDescription>
        <DialogFooter className={dialogFooterClassName}>
          <DialogClose asChild>
            <Button
              variant="secondary"
              size="sm"
              className={dialogButtonClassName}
              onClick={() => {
                setResponse(null);
              }}
            >
              <Trans>Done</Trans>
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    );
  }

  // --- Approval-based states (when requiresApproval is true) ---

  if (event.requiresApproval && event.approvalStatus === "pending") {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>Request pending</Trans></DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Trans>Your request is with the team. We'll confirm soon.</Trans>
        </DialogDescription>
        <EventDetails event={event} />
        <DialogFooter className={dialogFooterClassName}>
          <DialogClose asChild>
            <Button variant="outline" size="sm" className={dialogButtonClassName}>
              <Trans>Close</Trans>
            </Button>
          </DialogClose>
          <Button
            variant="secondary"
            size="sm"
            className={dialogButtonClassName}
            onClick={() => handleRSVP(false)}
          >
            <Trans>Withdraw request</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  if (event.requiresApproval && event.approvalStatus === "approved") {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>Approved. Ready to confirm?</Trans></DialogTitle>
        </DialogHeader>
        <DialogDescription><Trans>You're approved - lock in your spot.</Trans></DialogDescription>
        <EventDetails event={event} variant="attending" />
        <DialogFooter className={dialogFooterClassName}>
          <Button
            variant="outline"
            size="sm"
            className={dialogButtonClassName}
            onClick={() => handleRSVP(false)}
          >
            <Trans>Not this one</Trans>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className={dialogButtonClassName}
            onClick={() => handleRSVP(true)}
          >
            <Trans>I'm in</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  if (event.requiresApproval && event.approvalStatus === "rejected") {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>Update on your request to join</Trans></DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Trans>We couldn't approve this request.</Trans>
        </DialogDescription>
        <DialogFooter className={dialogFooterClassName}>
          <DialogClose asChild>
            <Button
              variant="secondary"
              size="sm"
              className={dialogButtonClassName}
            >
              <Trans>Close</Trans>
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    );
  }

  if (event.requiresApproval && event.approvalStatus === "cancelled") {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>Request cancelled</Trans></DialogTitle>
        </DialogHeader>
        <DialogDescription><Trans>You cancelled this request.</Trans></DialogDescription>
        <EventDetails event={event} />
        <DialogFooter className={dialogFooterClassName}>
          <DialogClose asChild>
            <Button
              variant="secondary"
              size="sm"
              className={dialogButtonClassName}
            >
              <Trans>Close</Trans>
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    );
  }

  // "confirmed" approval status is treated the same as attending
  if (event.attending || event.approvalStatus === "confirmed") {
    // TODO: Update stuff
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle><Trans>Coming along?</Trans></DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <Trans>Quick check: does the time and location still work for you?</Trans>
          {event.redirectURL && !isZoomFunnelEvent && (
            <span className="block mt-2 font-medium">
              <Trans>
                Heads up: registration continues at{" "}
                <a
                  href={event.redirectURL}
                  target="_blank"
                  className="break-all text-indigo-600 hover:underline"
                >
                  {event.redirectURL}
                </a>{" "}
                .
              </Trans>
            </span>
          )}
        </DialogDescription>
        <EventDetails event={event} variant="attending" />
        <DialogFooter className={dialogFooterClassName}>
          <Button
            variant="outline"
            size="sm"
            className={dialogButtonClassName}
            onClick={() => handleRSVP(false)}
          >
            <Trans>Not this one</Trans>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className={dialogButtonClassName}
            onClick={() => handleRSVP(true)}
          >
            <Trans>I'm in</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  // --- Default: not attending, no approval status ---

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle><Trans>Coming along?</Trans></DialogTitle>
      </DialogHeader>
      <DialogDescription>
        <Trans>Quick check: does the time and location still work for you?</Trans>
        {event.redirectURL && !isZoomFunnelEvent && (
          <span className="block mt-2 font-medium">
            <Trans>
              Heads up: registration continues at{" "}
              <a
                href={event.redirectURL}
                target="_blank"
                className="break-all text-indigo-600 hover:underline"
              >
                {event.redirectURL}
              </a>{" "}
              .
            </Trans>
          </span>
        )}
      </DialogDescription>
      <EventDetails event={event} />
      <DialogFooter className={dialogFooterClassName}>
        <DialogClose asChild>
          <Button variant="outline" size="sm" className={dialogButtonClassName}>
            <Trans>Not now</Trans>
          </Button>
        </DialogClose>
        <Button
          variant="secondary"
          size="sm"
          className={dialogButtonClassName}
          onClick={() => handleRSVP(true)}
        >
          {event.requiresApproval ? <Trans>Request to join</Trans> : <Trans>Save my spot</Trans>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
